import { FooterModalButtons } from "@/controls/buttons/FooterModalButtons";
import {
	ErrorModal,
	getMomentTz,
	HelmetDexteel,
	MesfModal,
} from "@dexteel/mesf-core";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { Alert, Grid2, Typography } from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import type { Inspection } from "../../models/Inspection";
import { deleteInspection } from "../../repositories/InspectionRepository";

const moment = getMomentTz();

type Props = {
	inspection: Inspection | null;
	open: boolean;
	onHide: () => void;
};

export const DeleteInspectionModal = ({ inspection, open, onHide }: Props) => {
	const queryClient = useQueryClient();

	const [error, setError] = useState("");

	const deleteMutation = useMutation(deleteInspection, {
		meta: { successMessage: "The inspection sheet was deleted successfully" },
		onSuccess: async () => {
			await queryClient.invalidateQueries({ queryKey: ["inspections"] });
			await queryClient.invalidateQueries({
				queryKey: ["inspection-sections"],
			});
			onHide();
		},
		onError: (mutationError: Error) => setError(mutationError.message),
	});

	const onSubmit = (event: React.FormEvent) => {
		event.preventDefault();
		if (!inspection) return;
		deleteMutation.mutate(inspection.InspectionId);
	};

	const readingCount = inspection?.RowCountTotal ?? 0;

	const facts = inspection
		? [
				`Rev ${inspection.Rev}`,
				inspection.AssetName,
				moment.utc(inspection.ProductionDate).format("ddd MM/DD/YYYY"),
				`Shift ${inspection.ProductionShift}${
					inspection.Crew ? ` - ${inspection.Crew}` : ""
				}`,
			]
		: [];

	return (
		<>
			{open && <HelmetDexteel title="Delete Sheet - Inspections Manager" />}
			<MesfModal
				id="modal-delete-inspection"
				title="DELETE SHEET"
				open={open}
				handleClose={onHide}
				maxWidth="sm"
			>
				<form onSubmit={onSubmit}>
					<MesfModal.Content style={{ padding: "15px 30px" }} dividers>
						<Grid2 container spacing={2}>
							<Grid2 size={{ xs: 12 }}>
								<Typography variant="body1">
									Delete the inspection sheet of{" "}
									<strong>{inspection?.SectionName ?? ""}</strong>?
								</Typography>
								<Typography variant="body2" color="text.secondary">
									{facts.join("  ·  ")}
								</Typography>
							</Grid2>
							<Grid2 size={{ xs: 12 }}>
								<Alert severity="warning" icon={<WarningAmberIcon />}>
									{readingCount > 0
										? `The ${readingCount} reading${
												readingCount === 1 ? "" : "s"
											} on it will be deleted too, with every value and surface defect taken. This cannot be undone.`
										: "It has no readings yet. This cannot be undone."}
								</Alert>
							</Grid2>
						</Grid2>
					</MesfModal.Content>
					<MesfModal.Actions style={{ padding: "20px 30px 30px" }}>
						<FooterModalButtons
							isSubmitLoading={deleteMutation.isLoading}
							onHide={onHide}
							buttonLabel="Delete"
							buttonColor="secondary"
							disabled={!inspection}
						/>
					</MesfModal.Actions>
				</form>
			</MesfModal>
			<ErrorModal error={error} onHide={() => setError("")} />
		</>
	);
};
